<template>
  <div 
    class="draft-menu" 
    v-show="visible" 
    :style="{ left: `${position.x}px`, top: `${position.y}px` }"
  >
    <!-- 编辑选项 - 只对普通节点显示 -->
    <div 
      class="item" 
      v-if="!currentNode?.data?.invisible && !currentNode?.data?.isGroup" 
      @click="handleMenuClick('edit')"
    >
      编辑
    </div>
    
    <!-- 取消组合 - 只对已组合的节点显示 -->
    <div 
      class="item" 
      v-if="currentNode?.data?.isGroup && currentNode?.data?.groupedNodes" 
      @click="handleMenuClick('ungroup')"
    >
      取消组合
    </div>
    
    <!-- 添加子元素 - 只对未组合的组节点显示 -->
    <div 
      class="item" 
      v-if="currentNode?.data?.isGroup && !currentNode?.data?.groupedNodes" 
      @click="handleMenuClick('add-child')"
    >
      添加子元素
    </div>
    
    <!-- 加边框 - 只对普通节点显示 -->
    <div 
      class="item" 
      v-if="!currentNode?.data?.invisible && !currentNode?.data?.isGroup" 
      @click="handleMenuClick('add-border')"
    >
      加边框
    </div>
    
    <!-- 删除 - 对所有节点显示 -->
    <div class="item" @click="handleMenuClick('delete')">
      删除
    </div>
  </div>
</template>

<script>
export default {
  name: 'DraftContextMenu',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    position: {
      type: Object,
      default: () => ({ x: 0, y: 0 })
    },
    currentNode: {
      type: Object,
      default: null
    },
    // 新增 props，用于接收 graph 实例
    graph: {
      type: Object,
      default: null,
      validator: () => true // 允许任何类型，包括 X6.Graph 实例
    }
  },
  emits: [
    'menu-click',
    'update:visible',
    // 新增事件
    'node-edit',
    'open-add-group',
    'node-remove',
    'node-remove-color'
  ],
  methods: {
    handleMenuClick(action) {
      // 根据不同的操作，调用对应的方法或发射事件
      switch (action) {
        case 'edit':
          this.nodeEdit()
          break
        case 'edit-group':
          this.editGroupCombo()
          break
        case 'ungroup':
          this.ungroupCurrentNode()
          break
        case 'add-child':
          this.openAddGroup()
          break
        case 'add-border':
          this.nodeRemoveColor()
          break
        case 'delete':
          this.nodeRemove()
          break
        default:
      }
      this.hideMenu()
    },
    
    hideMenu() {
      this.$emit('update:visible', false)
    },
    
    // 从 draft.vue 移过来的方法
    nodeEdit() {
      this.$emit('node-edit', this.currentNode)
    },
    
    nodeRemove() {
      if (!this.currentNode || !this.graph) {
        this.$emit('node-remove', this.currentNode)
        return
      }
      
      // 如果是组合节点，先删除其子节点
      if (this.currentNode.getData()?.isGroup && this.currentNode.getData()?.groupedNodes) {
        const groupedNodeIds = this.currentNode.getData().groupedNodes || []
        groupedNodeIds.forEach(nodeId => {
          const childNode = this.graph.getCellById(nodeId)
          if (childNode) {
            this.graph.removeCell(childNode)
          }
        })
      }
      
      this.graph.removeCell(this.currentNode)
      
      // 删除原有的子元素逻辑（用于其他类型的父子关系）
      const childrenList = this.graph.getCells().filter((item) => {
        return item?.data?.id.includes(this.currentNode.id)
      })
      if (childrenList.length) {
        childrenList.forEach((item) => {
          this.graph.removeCell(item)
        })
      }
      
      this.$emit('node-remove', this.currentNode)
    },
    
    nodeRemoveColor() {
      if (!this.currentNode) {
        this.$emit('node-remove-color', this.currentNode)
        return
      }
      
      this.currentNode.attr('body/fill', 'transparent')
      this.currentNode.attr('body/strokeWidth', 1)
      this.currentNode.attr('body/stroke', '#000')
      this.currentNode.attr('text/fill', '#000')
      
      this.$emit('node-remove-color', this.currentNode)
    },
    
    openAddGroup() {
      this.$emit('open-add-group', this.currentNode)
    },
    
    editGroupCombo() {
      this.$emit('menu-click', {
        action: 'edit-group',
        node: this.currentNode
      })
    },
    
    ungroupCurrentNode() {
      this.$emit('menu-click', {
        action: 'ungroup',
        node: this.currentNode
      })
    }
  },
  computed: {
    // 计算菜单项的显示状态，便于调试和扩展
    menuItems() {
      if (!this.currentNode) return []
      
      const nodeData = this.currentNode.data || {}
      const items = []
      
      // 普通节点的编辑选项
      if (!nodeData.invisible && !nodeData.isGroup) {
        items.push({ key: 'edit', label: '编辑' })
        items.push({ key: 'add-border', label: '加边框' })
      }
      
      // 组合节点的选项
      if (nodeData.isGroup) {
        if (nodeData.groupedNodes) {
          items.push({ key: 'edit-group', label: '编辑组合' })
          items.push({ key: 'ungroup', label: '取消组合' })
        } else {
          items.push({ key: 'add-child', label: '添加子元素' })
        }
      }
      
      // 删除选项对所有节点都显示
      items.push({ key: 'delete', label: '删除' })
      
      return items
    }
  }
}
</script>

<style lang="scss" scoped>
.draft-menu {
  position: absolute;
  background-color: #fff;
  border: 1px solid #ccc;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.2);
  z-index: 99;
  border-radius: 4px;
  min-width: 80px;

  .item {
    font-size: 13px;
    width: 70px;
    height: 20px;
    text-align: center;
    padding: 8px;
    cursor: pointer;
    white-space: nowrap;
    user-select: none;
    transition: all 0.2s ease;

    &:first-child {
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
    }

    &:last-child {
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
    }

    &:hover {
      background-color: #ecf5ff;
      color: #66b1ff;
    }

    &:active {
      background-color: #d9ecff;
    }
  }
}
</style>


